import { useState, useEffect } from 'react';
import { useAuthorizationContext } from '@digital-services/shell';
import useFetchUserOrganizations from '@/api/use-fetch-user-organizations';

export interface IUseApplicationRoles {
  roles: string[];
  isLoading: boolean;
  error: Error | null;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
}

export function useApplicationRoles(): IUseApplicationRoles {
  const { getAccessToken, userData } = useAuthorizationContext();
  const fetchUserOrganizations = useFetchUserOrganizations();
  const [roles, setRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userData) return;
    
    const loadApplicationRoles = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const fetchedOrganizations = await fetchUserOrganizations(getAccessToken);
        const userOrganization = fetchedOrganizations.find(organization => organization.organization.id === userData.organizationId);
        setRoles(userOrganization?.applicationRoles || []);
      } catch (err) {
        setRoles([]);
        setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      } finally {
        setIsLoading(false);
      }
    };

    loadApplicationRoles();
  // eslint-disable-next-line react-compiler/react-compiler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, getAccessToken]);

  const hasRole = (role: string): boolean => {
    return roles.includes(role);
  };

  const hasAnyRole = (rolesToCheck: string[]): boolean => {
    return rolesToCheck.some(role => roles.includes(role));
  };

  const hasAllRoles = (rolesToCheck: string[]): boolean => {
    return rolesToCheck.every(role => roles.includes(role));
  };

  return {
    roles,
    isLoading,
    error,
    hasRole,
    hasAnyRole,
    hasAllRoles
  };
}